import { useCallback, useState } from "react";
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";

export type ActiveDrag = {
  type: "asset" | "sortable-asset";
  id: string;
  socketId?: string;
  originalIndex?: number;
  connectorIndex?: number;
  overIndex?: number;
};

export function useSocketDragHandlers(
  draftMap: Record<string, string[]>,
  addAssetToRow: (socketId: string, assetId: string) => void,
  reorderAssets: (socketId: string, oldIndex: number, newIndex: number) => void,
) {
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current as
        | { type: "asset"; assetId: string }
        | { type: "sortable-asset"; socketId: string; assetId: string }
        | undefined;
      if (!data) {
        return;
      }
      if (data.type === "sortable-asset") {
        const row = draftMap[data.socketId];
        const idx = row?.indexOf(data.assetId) ?? -1;
        setActiveDrag({
          type: "sortable-asset",
          id: data.assetId,
          socketId: data.socketId,
          originalIndex: idx,
          connectorIndex: idx + 1,
          overIndex: idx,
        });
      } else {
        setActiveDrag({ type: "asset", id: data.assetId });
      }
    },
    [draftMap],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event;
      if (!over) {
        return;
      }
      setActiveDrag((prev) => {
        if (!prev || prev.type !== "sortable-asset") {
          return prev;
        }
        const overData = over.data.current as
          | { type: "sortable-asset"; socketId: string; assetId: string }
          | undefined;
        if (
          overData?.type === "sortable-asset" &&
          overData.socketId === prev.socketId
        ) {
          const row = draftMap[overData.socketId];
          if (row) {
            const newIndex = row.indexOf(overData.assetId);
            if (newIndex !== -1 && prev.overIndex !== newIndex) {
              return {
                ...prev,
                connectorIndex: newIndex + 1,
                overIndex: newIndex,
              };
            }
          }
        }
        return prev;
      });
    },
    [draftMap],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDrag(null);
      const { active, over } = event;
      if (!over) {
        return;
      }

      const activeData = active.data.current as
        | { type: "asset"; assetId: string }
        | { type: "sortable-asset"; socketId: string; assetId: string }
        | undefined;
      if (!activeData) {
        return;
      }

      if (
        activeData.type === "asset" &&
        typeof over.id === "string" &&
        over.id.startsWith("row-")
      ) {
        const socketId = over.id.slice(4);
        addAssetToRow(socketId, activeData.assetId);
      } else if (activeData.type === "sortable-asset") {
        const overData = over.data.current as
          | { type: "sortable-asset"; socketId: string; assetId: string }
          | undefined;
        if (
          overData?.type === "sortable-asset" &&
          overData.socketId === activeData.socketId &&
          activeData.assetId !== overData.assetId
        ) {
          const row = draftMap[activeData.socketId];
          if (row) {
            const oldIndex = row.indexOf(activeData.assetId);
            const newIndex = row.indexOf(overData.assetId);
            if (oldIndex !== -1 && newIndex !== -1) {
              reorderAssets(activeData.socketId, oldIndex, newIndex);
            }
          }
        }
      }
    },
    [addAssetToRow, reorderAssets, draftMap],
  );

  const getVisualConnector = useCallback(
    (socketId: string, actualIndex: number) => {
      if (
        !activeDrag ||
        activeDrag.type !== "sortable-asset" ||
        activeDrag.socketId !== socketId ||
        activeDrag.originalIndex == null ||
        activeDrag.overIndex == null
      ) {
        return { num: actualIndex + 1, shifted: false };
      }
      const orig = activeDrag.originalIndex;
      const over = activeDrag.overIndex;
      if (orig === over) {
        return { num: actualIndex + 1, shifted: false };
      }
      if (actualIndex === orig) {
        return { num: over + 1, shifted: true };
      }
      if (orig < over) {
        if (actualIndex > orig && actualIndex <= over) {
          return { num: actualIndex, shifted: true };
        }
      } else {
        if (actualIndex >= over && actualIndex < orig) {
          return { num: actualIndex + 2, shifted: true };
        }
      }
      return { num: actualIndex + 1, shifted: false };
    },
    [activeDrag],
  );

  return {
    activeDrag,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getVisualConnector,
  };
}
